import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useMenuConfig } from "../hooks/useMenuConfig";
import styles from "./LayoutBase.module.css";
import Logo from '../assets/logo.png';
import OfflineAlert from "../components/verificarede/offlineAlert";
import api from "../services/api";
import { isVLibrasOculto, setVLibrasOculto } from "../services/vlibrasPreferencia";

const APP_VERSION = "1.7.0";

export default function LayoutBase({ children }) {
    const { user, logout } = useContext(AuthContext);
    const [menuOpen, setMenuOpen] = useState(false);
    // Seção com submenu expandido inline no próprio drawer (sanfona) - id da
    // seção, ou null se nenhuma estiver aberta. Substituiu o SectionSheet (modal
    // de baixo) que era usado aqui antes - o BottomNav continua usando o sheet
    // normalmente, só o drawer lateral virou sanfona (pedido do usuário).
    const [secaoExpandida, setSecaoExpandida] = useState(null);
    // Toggle de "esconder o ícone do VLibras" (pedido do usuário, 2026-09-18) -
    // guardado em localStorage via vlibrasPreferencia.js, lido aqui só pra
    // desenhar o estado inicial do switch.
    const [vlibrasOculto, setVlibrasOcultoState] = useState(isVLibrasOculto);
    const navigate = useNavigate();
    const location = useLocation();
    const [foto, setFoto] = useState(() => localStorage.getItem("fotoFuncionario"));

    const menuConfig = useMenuConfig();

    useEffect(() => {
        if (foto) return;
        api.get(`/colaborador/getfoto/?codfun=${user.codfun}&tipfun=${user.tipfun}`)
            .then(res => {
                localStorage.setItem("fotoFuncionario", res.data.fotfun);
                setFoto(res.data.fotfun);
            })
            .catch(() => { });
    }, []);

    function fechar() {
        setMenuOpen(false);
        setSecaoExpandida(null);
    }

    function handleSectionClick(section) {
        // seção com 1 item e rota direta: navega direto, sem sanfona
        if (section.items.length === 1 && section.items[0].rota) {
            fechar();
            const item = section.items[0];
            navigate(item.rota, { state: { titulo: item.label, icone: item.icon, subtitulo: item.subtitulo } });
            return;
        }
        // alterna a sanfona dessa seção - drawer continua aberto
        setSecaoExpandida(prev => prev === section.id ? null : section.id);
    }

    function handleItemClick(item) {
        fechar();
        if (item.rota) {
            // Mesmo mecanismo do item tipo 'conteudo' logo abaixo - a página de
            // destino lê o header (título/ícone/subtítulo) do state da navegação
            // em vez de ter texto fixo (pedido do usuário, 2026-10-07: "vem do
            // que eu coloco em título e subtítulo e ícone no cozimaxerp").
            navigate(item.rota, { state: { titulo: item.label, icone: item.icon, subtitulo: item.subtitulo } });
        } else if (item.conteudo) {
            navigate("/menu-conteudo", { state: { item: item.conteudo, titulo: item.label, icone: item.icon } });
        } else {
            window.open(item.url, "_blank");
        }
    }

    function handleLogout() { logout(); navigate("/"); }

    function toggleVLibras() {
        const novoValor = !vlibrasOculto;
        setVLibrasOculto(novoValor);

        // Ligar funciona na hora (é uma inicialização nova do plugin). Desligar
        // não - o VLibras não tem uma função pública pra "desinicializar" o
        // botão que ele já desenhou, e nenhum CSS daqui alcança de verdade
        // (ver VLibrasWidget.jsx). Um reload reproduz exatamente o "fechar e
        // abrir o app" que já confirmadamente esconde - VLibrasWidget lê a
        // preferência antes de sequer carregar o script do plugin.
        if (novoValor) {
            window.location.reload();
            return;
        }

        setVlibrasOcultoState(novoValor);
    }

    // Prefixo, não só igualdade exata (2026-10-14) - mesmo motivo do BottomNav:
    // subpágina (ex: /refeicao/almoco) não pode apagar o destaque da seção.
    function isActive(section) {
        return section.items.some(item =>
            item.rota && (location.pathname === item.rota || location.pathname.startsWith(item.rota + "/"))
        );
    }

    return (
        <div className={styles.container}>

            <OfflineAlert />

            {/* HEADER FIXO */}
            <div className={styles.header}>
                <div className={styles.headerTop}>
                    <button className={styles.menuButton} onClick={() => setMenuOpen(true)}>
                        ☰
                    </button>
                    <div className={styles.logoArea}>
                        <img src={Logo} alt="Logo Empresa" />
                    </div>
                </div>
                <div className={styles.brandBar} />
            </div>

            {/* OVERLAY DRAWER */}
            {menuOpen && <div className={styles.overlay} onClick={fechar} />}

            {/* DRAWER */}
            <div className={`${styles.drawer} ${menuOpen ? styles.open : ""}`}>

                {/* PERFIL */}
                <div className={styles.drawerUser}>
                    <div className={styles.drawerAvatar}>
                        {foto
                            ? <img src={`data:image/jpeg;base64,${foto}`} alt="Foto" className={styles.drawerAvatarImg} />
                            : <span>{user?.nomfun?.charAt(0)}</span>
                        }
                    </div>
                    <div className={styles.drawerUserInfo}>
                        <strong>{user?.nomfun}</strong>
                        <span>{user?.setor}</span>
                        {(user?.funcao || user?.desfun) && (
                            <span className={styles.drawerUserRole}>{user?.funcao || user?.desfun}</span>
                        )}
                    </div>
                </div>

                {/* SECTIONS */}
                <nav className={styles.drawerNav}>
                    {menuConfig.filter(section => section.navLateral).map((section) => {
                        const active = isActive(section);
                        const aberta = secaoExpandida === section.id;
                        return (
                            <div key={section.id ?? section.section}>
                                <div
                                    className={`${styles.drawerSection} ${active ? styles.drawerSectionActive : ""}`}
                                    onClick={() => handleSectionClick(section)}
                                >
                                    <span className={styles.drawerSectionIcon}>{section.icon}</span>
                                    <span className={styles.drawerSectionLabel}>{section.section}</span>
                                    <span className={styles.drawerSectionCount}>
                                        {section.items.length > 1 ? `${section.items.length}` : ""}
                                    </span>
                                    {section.items.length > 1 || !section.items[0]?.rota ? (
                                        <span className={`${styles.drawerSectionArrow} ${aberta ? styles.drawerSectionArrowAberta : ""}`}>▼</span>
                                    ) : (
                                        <span className={styles.drawerSectionArrow}>›</span>
                                    )}
                                </div>

                                {/* SUBMENU (sanfona) - só existe pra seção com mais de 1 item, ou
                                    com 1 item que não seja rota direta (essas navegam sem abrir) */}
                                {aberta && (
                                    <div className={styles.drawerSubmenu}>
                                        {section.items.map((item) => (
                                            <div
                                                key={item.label}
                                                className={styles.drawerSubmenuItem}
                                                onClick={() => handleItemClick(item)}
                                            >
                                                <span className={styles.drawerSubmenuIcon}>{item.icon}</span>
                                                <span className={styles.drawerSubmenuLabel}>{item.label}</span>
                                                {item.url && <span className={styles.drawerSubmenuExterno}>↗</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>

                {/* ACESSIBILIDADE - esconder/mostrar o ícone do VLibras (pedido
                    do usuário, 2026-09-18) */}
                <div className={styles.drawerAcessibilidade}>
                    <span className={styles.drawerAcessibilidadeLabel}>🤟 Tradução em Libras</span>
                    <button
                        className={`${styles.vlibrasSwitch} ${!vlibrasOculto ? styles.vlibrasSwitchAtivo : ""}`}
                        onClick={toggleVLibras}
                        aria-pressed={!vlibrasOculto}
                        aria-label="Mostrar ou esconder o ícone do VLibras"
                    >
                        <span className={styles.vlibrasSwitchBolinha} />
                    </button>
                </div>

                {/* Diagnóstico de notificações push - link discreto pra quem tiver
                    problema conseguir ver o motivo direto no celular (pedido do
                    usuário, 2026-09-28: "não consigo ver o console") */}
                <div
                    className={styles.drawerDiagnostico}
                    onClick={() => { fechar(); navigate("/diagnostico-push"); }}
                >
                    🔧 Diagnóstico de notificações
                </div>

                {/* RODAPÉ */}
                <div className={styles.drawerFooter}>
                    <button className={styles.btnSair} onClick={handleLogout}>
                        <span>🚪</span>
                        <span>Sair</span>
                    </button>
                    <span className={styles.drawerVersion}>v{APP_VERSION}</span>
                </div>
            </div>

            {/* CONTEÚDO */}
            <div className={styles.content}>
                {children}
            </div>

        </div>
    );
}
